<template>
	<core-layout-content full>
		<!-- Headline -->
		<core-layout-headline-and-actions>
			<template slot="headline">{{ $t("plans") }}</template>
			<template slot="actions">
				<j-button v-can="'writePlans'" @click="$modal.show('createPlan')">
					<crm-icon name="add" size="small" color="c5" />
					{{ $t("create") }}
				</j-button>
			</template>
		</core-layout-headline-and-actions>

		<!-- Search -->
		<div class="flex wrap xs-12 xs-left mb2">
			<div class="flex xs-12">
				<j-base-input-search
					:placeholder="$t('search_plans')"
					:value="searchTerm"
					@input="onSearch($event)"
				/>
				<j-button
					class="ml2"
					type="tertiary"
					v-if="searchTerm"
					@click="cancelSearch"
					>{{ $t("cancel") }}</j-button
				>
			</div>
		</div>

		<!-- Data Container -->
		<j-card class="bg5">
			<j-data-table
				:header="header"
				:list="computedList"
				:sortable="false"
				:isLoading="isLoading"
				@sort="onSort($event)"
			>
				<template slot="list" slot-scope="{ item }">
					<td v-for="(column, i) in header" :key="i">
						<template>
							<!-- Name -->
							<template v-if="column.field === 'name'">
								<router-link
									:to="{
										name: `PlanDetail`,
										params: {
											plan: item.id.plan,
											type: item.id.type,
											version: item.id.version
										}
									}"
									>{{ item.name ? item.name : "---" }}</router-link
								>
							</template>

							<!-- Others -->
							<template v-else>{{ item[column.field] }}</template>

							<!-- Actions Field -->
							<template v-if="column.field === 'actions'">
								<div class="action">
									<!-- Delete -->
									<crm-icon
										name="delete"
										size="normal"
										color="cError"
										@click.native="showModal(item.id)"
										v-can="'writePlans'"
									/>
								</div>
							</template>
						</template>
					</td>
				</template>
				<template slot="empty">{{ $t("errors.no_data") }}</template>
			</j-data-table>
		</j-card>

		<!-- Delete confirmation modal -->
		<j-dialog
			:height="200"
			:width="600"
			:title="$t('delete')"
			type="delete"
			@confirm="deletePlan(selectedItemId)"
			>{{ $t("errors.are_you_sure_you_want_to_delete_plan") }}</j-dialog
		>

		<!-- Create Plan Modal -->
		<core-modal name="createPlan" :headline="$t('create_plan')">
			<plan-form @submit="createPlan">
				<div slot-scope="{ onSubmit }">
					<j-button @click="onSubmit" class="mt6" :isLoading="isLoading">{{
						$t("create")
					}}</j-button>
				</div>
			</plan-form>
		</core-modal>
	</core-layout-content>
</template>

<script>
import CoreLayoutContent from "@/modules/core/components/CoreLayoutContent"
import CoreFormInput from "@/modules/core/components/CoreFormInput"
import CoreModal from "@/modules/core/components/CoreModal"
import CoreLayoutHeadlineAndActions from "@/modules/core/components/CoreLayoutHeadlineAndActions"
import JButton from "@/components/JButton/"
import CrmIcon from "@/components/CrmIcon/"
import JCard from "@/components/JCard/"
import JDataTable from "@/components/JDataTable/"
import JDialog from "@/components/JDialog/"
import JBaseInputSearch from "@/components/JBaseInputSearch/"
import PlanForm from "../components/PlanForm"

import dayjs from "dayjs"
import debounce from "lodash.debounce"

import api from "../api"

export default {
	components: {
		CoreLayoutContent,
		CoreFormInput,
		CoreModal,
		CoreLayoutHeadlineAndActions,
		JButton,
		CrmIcon,
		JCard,
		JDataTable,
		JDialog,
		JBaseInputSearch,
		PlanForm
	},

	data() {
		return {
			isLoading: false,

			// Actual fetched data
			data: [],

			// Search term
			searchTerm: null,

			// Header of table
			header: [
				{
					field: "name",
					text: this.$t("name")
				},
				{
					field: "type",
					text: this.$t("type")
				},
				{
					field: "createdAt",
					text: this.$t("created")
				},
				{
					field: "actions",
					text: this.$t("actions")
				}
			],

			selectedItemId: null
		}
	},

	computed: {
		computedList() {
			if (!this.data) return []

			return this.data.map(item => ({
				id: item.id,
				name: item.name,
				createdAt: item.createdAt
					? dayjs(item.createdAt).format(
							this.$store.state.settings.dateTimeFormat
					  )
					: null,
				type: item.id && item.id.type
			}))
		},

		computedWhere() {
			return this.searchTerm
				? {
						and: [
							{
								name_contains: this.searchTerm
							},
							{
								tags_some: { type: "plan" }
							}
						]
				  }
				: {
						tags_some: { type: "plan" }
				  }
		}
	},

	mounted() {
		this.fetchPlans()
	},

	methods: {
		scrollToTop() {
			this.$scrollTo("body", 300)
		},

		onSearch: debounce(function(value) {
			this.searchTerm = value
			this.fetchPlans()
		}, 200),

		cancelSearch() {
			this.searchTerm = null
			this.fetchPlans()
		},

		async createPlan(payload) {
			this.isLoading = true

			const res = await api.createPlan({
				input: {
					productId: {
						type: payload.type,
						plan: payload.plan,
						version: payload.version
					},
					// NOTE: adding plan tag to created plans
					tags: [{ type: "plan" }]
				}
			})

			if (res?.data?.createPlan?.status !== "success") {
				this.$store.dispatch("addMessage", {
					message: {
						type: "error",
						text:
							res?.errors ||
							res?.data?.createPlan?.errors ||
							res?.data?.createPlan?.errors_2?.message ||
							"Oops ! An error occured."
					}
				})
				return
			}

			// localize plan by upserting name and description
			const res2 = await api.localizePlan({
				l10n: {
					key: `products-${payload.plan}|${payload.version || ""}|${
						payload.type
					}-name`,
					locale: payload.locale,
					value: payload.name
				}
			})

			if (res2?.data?.upsertL10n?.status !== "success") {
				this.$store.dispatch("addMessage", {
					message: {
						type: "error",
						text:
							res?.errors ||
							res?.data?.createPlan?.errors ||
							res?.data?.createPlan?.errors_2?.message ||
							"Oops ! An error occured."
					}
				})
				return
			}

			const res3 = await api.localizePlan({
				l10n: {
					key: `products-${payload.plan}|${payload.version || ""}|${
						payload.type
					}-description`,
					locale: payload.locale,
					value: payload.description
				}
			})

			if (res3?.data?.upsertL10n?.status !== "success") {
				this.$store.dispatch("addMessage", {
					message: {
						type: "error",
						text:
							res?.errors ||
							res?.data?.createPlan?.errors ||
							res?.data?.createPlan?.errors_2?.message ||
							"Oops ! An error occured."
					}
				})
				return
			}

			this.$store.dispatch("addMessage", {
				message: {
					type: "success",
					text: this.$t("plan_created")
				}
			})

			this.fetchPlans()

			this.isLoading = false

			this.$modal.hide("createPlan")
		},

		async deletePlan(id) {
			this.isLoading = true
			let res = await api.deletePlan({ id })

			if (res?.data?.deletePlan?.status !== "success") {
				this.$store.dispatch("addMessage", {
					message: {
						type: "error",
						text:
							res?.errors ||
							res?.data?.deletePlan?.errors ||
							res?.data?.deletePlan?.errors_2?.message ||
							"Oops ! An error occured."
					}
				})
			} else
				this.$store.dispatch("addMessage", {
					message: {
						type: "success",
						text: this.$t("plan_deleted")
					}
				})

			// Delete locales
			let variables = {
				key: `products-${id.plan}||${id.type}-name`
			}
			res = await api.deleteAllL10n(variables)
			variables = {
				key: `products-${id.plan}||${id.type}-description`
			}
			res = await api.deleteAllL10n(variables)

			this.fetchPlans()
			this.isLoading = false
			this.hideModal()
		},

		async fetchPlans() {
			// Sorting - sort by createdAt
			let sort = { fieldName: "createdAt", type: "desc" }

			// Search
			const where = this.computedWhere

			this.isLoading = true
			const res = await api.getPlans({ sort, where })
			this.isLoading = false

			// Check errors
			if (res.errors)
				this.$store.dispatch("showError", [res.errors?.[0]?.message])

			this.data = res?.data?.plans || []
		},

		showModal(id) {
			this.selectedItemId = id
			this.$modal.show("j-dialog")
		},

		hideModal() {
			this.selectedItemId = null
			this.$modal.hide("j-dialog")
		}
	}
}
</script>
